import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '@/services/api';
import { clearGame } from './gameSlice';
import type { SignInRequest, AuthTokens, User } from '@/types';
import {
  consumeOAuthFragment,
  isAllowedEmail,
  isNexusAccessToken,
  loginPassword,
  logoutSession,
  rememberOAuthError,
  userFromAccessToken,
} from '@/auth/nexusAuth';

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

function persistSession(tokens: AuthTokens, user: User): void {
  localStorage.setItem('auth_tokens', JSON.stringify(tokens));
  localStorage.setItem('auth_user', JSON.stringify(user));
  apiClient.setAccessToken(tokens.accessToken);
}

function clearPersistedSession(): void {
  localStorage.removeItem('auth_tokens');
  localStorage.removeItem('auth_user');
  apiClient.setAccessToken(null);
}

function sessionFromStorage(): { tokens: AuthTokens; user: User } | null {
  try {
    const rawTokens = localStorage.getItem('auth_tokens');
    const rawUser = localStorage.getItem('auth_user');
    if (!rawTokens || !rawUser) return null;
    const tokens = JSON.parse(rawTokens) as AuthTokens;
    const user = JSON.parse(rawUser) as User;
    if (!isNexusAccessToken(tokens.accessToken) || !isAllowedEmail(user.email)) {
      clearPersistedSession();
      return null;
    }
    if (!user.name && tokens.idToken) {
      user.name = userFromAccessToken(tokens.accessToken, tokens.idToken)?.name;
    }
    return { tokens, user };
  } catch {
    clearPersistedSession();
    return null;
  }
}

// Google/Apple returns land on /#oauth_result=… before React mounts.
const oauthOnLoad = consumeOAuthFragment();
if (oauthOnLoad && 'tokens' in oauthOnLoad) {
  persistSession(oauthOnLoad.tokens, oauthOnLoad.user);
} else if (oauthOnLoad && 'error' in oauthOnLoad) {
  clearPersistedSession();
  rememberOAuthError(oauthOnLoad.error);
}

const stored = sessionFromStorage();
if (stored) apiClient.setAccessToken(stored.tokens.accessToken);

const initialAuthState: AuthState = stored
  ? { ...initialState, tokens: stored.tokens, user: stored.user, isAuthenticated: true }
  : initialState;

const NOT_AUTHORIZED = 'This account is not authorized for R.U.S.H.';

export const signUp = createAsyncThunk('auth/signUp', async () => {
  throw new Error('Registration is not open.');
});

export const signIn = createAsyncThunk(
  'auth/signIn',
  async (data: SignInRequest, { rejectWithValue }) => {
    const email = data.email.trim().toLowerCase();
    if (!isAllowedEmail(email)) {
      return rejectWithValue(NOT_AUTHORIZED);
    }
    try {
      const tokens = await loginPassword(email, data.password);
      const user = userFromAccessToken(tokens.accessToken, tokens.idToken);
      if (!user || !isAllowedEmail(user.email)) {
        clearPersistedSession();
        return rejectWithValue(NOT_AUTHORIZED);
      }
      persistSession(tokens, user);
      return { tokens, user };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Sign in failed';
      return rejectWithValue(message);
    }
  }
);

export const signOut = createAsyncThunk('auth/signOut', async (_, { dispatch, getState }) => {
  const state = getState() as { auth: AuthState };
  await logoutSession(state.auth.tokens?.refreshToken);
  clearPersistedSession();
  dispatch(clearGame());
});

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUp.rejected, (state) => {
        state.isLoading = false;
        state.error = 'Registration is not open.';
      })
      .addCase(signIn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tokens = action.payload.tokens;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.error = null;
      });
  },
});

export const { clearError } = authSlice.actions;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export default authSlice.reducer;
